import { useEffect, useState } from "react";
import { getRiderProfileChangeRequest, submitRiderProfileChangeRequest, changeRiderPassword } from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import RiderNav from "../../components/RiderNav";
import RiderProfileStatus from "../../components/RiderProfileStatus";
import LoadingIndicator from "../../components/LoadingIndicator";

// Collapsible section — keeps the long KYC form from being one unbroken
// wall of fields; each block can be closed once reviewed/filled.
function Collapsible({ title, defaultOpen = false, className = "quote-box", style, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={className} style={{ marginTop: "16px", padding: 0, overflow: "hidden", ...style }}>
      <button
        type="button"
        className="secondary"
        onClick={() => setOpen(o => !o)}
        style={{ width: "100%", margin: 0, borderRadius: 0, border: "none", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px" }}
      >
        <span style={{ fontWeight: 600 }}>{title}</span>
        <span>{open ? "▲" : "▼"}</span>
      </button>
      {open && <div style={{ padding: "0 16px 16px" }}>{children}</div>}
    </div>
  );
}

const VEHICLE_TYPES = [
  { value: "bicycle", label: "🚲 Bicycle" },
  { value: "bike", label: "🏍️ Motorcycle / Bike" },
  { value: "van", label: "🚐 Cargo Van" },
  { value: "truck", label: "🚚 Delivery Truck" }
];
const EXPERIENCE_OPTIONS = [
  { value: "0", label: "0 (No experience)" },
  { value: "1-2", label: "1-2 Years" },
  { value: "3-5", label: "3-5 Years" },
  { value: "5+", label: "Above 5 Years" }
];

const emptyKyc = {
  surname: "", firstName: "", middleName: "", gender: "", maritalStatus: "", whatsappNumber: "",
  nin: "", dateOfBirth: "", residentialAddress: "", drivingLicenseNumber: "", vehicleOwnership: "",
  vehicleRegistrationNumber: "", ridingExperienceYears: "", drivingExperienceYears: "", hasValidLasdri: "",
  guarantor: { name: "", phone: "", address: "" }
};

function toDateInputValue(d) {
  if (!d) return "";
  return new Date(d).toISOString().slice(0, 10);
}

export default function RiderProfile() {
  const { account } = useAuth();
  const [current, setCurrent] = useState(null);
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({ name: "", phone: "", vehicleType: "bike", vehiclePlateNumber: "", kyc: emptyKyc });
  const [savingRequest, setSavingRequest] = useState(false);
  const [requestMessage, setRequestMessage] = useState("");
  const [requestError, setRequestError] = useState("");

  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await getRiderProfileChangeRequest();
      setCurrent(data.current);
      setRequest(data.request?.status === "none" ? null : data.request);
      const kyc = data.current.kyc || {};
      setForm({
        name: data.current.name || "",
        phone: data.current.phone || "",
        vehicleType: data.current.vehicleType || "bike",
        vehiclePlateNumber: data.current.vehiclePlateNumber || "",
        kyc: {
          ...emptyKyc,
          ...kyc,
          dateOfBirth: toDateInputValue(kyc.dateOfBirth),
          hasValidLasdri: kyc.hasValidLasdri === true ? "yes" : kyc.hasValidLasdri === false ? "no" : "",
          guarantor: { ...emptyKyc.guarantor, ...(kyc.guarantor || {}) }
        }
      });
    } catch {
      setRequestError("Could not load profile details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setKyc = (field, value) => setForm(f => ({ ...f, kyc: { ...f.kyc, [field]: value } }));
  const setGuarantor = (field, value) => setForm(f => ({ ...f, kyc: { ...f.kyc, guarantor: { ...f.kyc.guarantor, [field]: value } } }));

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setRequestError("");
    setRequestMessage("");
    setSavingRequest(true);
    try {
      // Derive name/plate from the KYC fields only when those are actually
      // filled in — a rider who hasn't completed KYC yet and is just
      // updating, say, their phone number shouldn't have their existing
      // name/plate blanked out by empty KYC fields.
      const derivedName = [form.kyc.surname, form.kyc.firstName, form.kyc.middleName].filter(Boolean).join(" ");
      await submitRiderProfileChangeRequest({
        name: derivedName || undefined,
        phone: form.phone,
        vehicleType: form.vehicleType,
        vehiclePlateNumber: form.kyc.vehicleRegistrationNumber || undefined,
        kyc: { ...form.kyc, hasValidLasdri: form.kyc.hasValidLasdri === "yes" ? true : form.kyc.hasValidLasdri === "no" ? false : undefined }
      });
      setRequestMessage("✓ Change request submitted — an admin will review it shortly.");
      await load();
    } catch (err) {
      setRequestError(err.response?.data?.error || "Could not submit change request.");
    } finally {
      setSavingRequest(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordMessage("");
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New password and confirmation do not match.");
      return;
    }
    setSavingPassword(true);
    try {
      await changeRiderPassword(passwordForm.currentPassword, passwordForm.newPassword);
      setPasswordMessage("✓ Password changed successfully.");
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      setPasswordError(err.response?.data?.error || "Could not change password.");
    } finally {
      setSavingPassword(false);
    }
  };

  const rc = request?.requestedChanges;
  const rk = rc?.kyc || {};

  return (
    <div className="rider-app">
      <RiderNav />
      <div className="page wide page-with-nav">
        <RiderProfileStatus />
        <div className="brand-header">
          <span className="brand-logo">👤</span>
          <span className="brand-name">My Profile</span>
          <p className="brand-tagline">Identity, vehicle &amp; KYC details require admin approval to change</p>
        </div>

        {loading ? (
          <LoadingIndicator label="Loading profile..." />
        ) : (
          <>
            {request?.status === "pending" && (
              <div className="quote-box" style={{ marginBottom: "16px", background: "rgba(250,84,28,0.08)" }}>
                <p style={{ fontWeight: 600 }}>⏳ Your change request is pending admin review.</p>
                <p className="hint">
                  Requested: {rc?.name && `Name → ${rc.name}. `}
                  {rc?.phone && `Phone → ${rc.phone}. `}
                  {rc?.vehicleType && `Vehicle → ${rc.vehicleType}. `}
                  {rc?.vehiclePlateNumber && `Plate → ${rc.vehiclePlateNumber}. `}
                  {Object.keys(rk).filter(k => k !== "guarantor").length > 0 && `${Object.keys(rk).filter(k => k !== "guarantor").length} KYC field(s) updated. `}
                  {rk.guarantor && Object.keys(rk.guarantor).length > 0 && "Guarantor details updated."}
                </p>
              </div>
            )}
            {request?.status === "rejected" && (
              <div className="quote-box" style={{ marginBottom: "16px", background: "rgba(239,68,68,0.08)" }}>
                <p style={{ fontWeight: 600, color: "var(--danger-hover)" }}>Your last change request was rejected by an admin.</p>
              </div>
            )}

            <Collapsible title="👤 Current Details" className="delivery-card" defaultOpen style={{ marginBottom: "24px" }}>
              <div className="info-row"><span className="label">Name</span><span className="value">{current?.name}</span></div>
              <div className="info-row"><span className="label">Phone</span><span className="value">{current?.phone}</span></div>
              <div className="info-row"><span className="label">Email</span><span className="value">{account?.email}</span></div>
              <div className="info-row"><span className="label">Vehicle</span><span className="value">{current?.vehicleType?.toUpperCase()} ({current?.vehiclePlateNumber || "No Plate"})</span></div>
            </Collapsible>

            <form onSubmit={handleRequestSubmit} className="delivery-card" style={{ marginBottom: "24px" }}>
              <h3>Request Profile Change</h3>
              <p className="hint" style={{ marginBottom: "10px" }}>
                Identity, vehicle, and KYC changes need admin approval — submit your requested details below.
              </p>

              {/* Name and plate number aren't edited here — they're the same
                  detail as Surname/First/Middle Name and "Registration
                  Number of Motorbike/Vehicle" in the KYC sections below, so
                  editing those updates these automatically instead of
                  asking for the same information twice. */}
              <div className="form-field">
                <label>Phone Number</label>
                <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
              <div className="form-field">
                <label>Vehicle Type</label>
                <select value={form.vehicleType} onChange={e => setForm(f => ({ ...f, vehicleType: e.target.value }))}>
                  {VEHICLE_TYPES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </div>

              <Collapsible title="🪪 KYC — Personal Information" defaultOpen>
                <div className="form-field">
                  <label>Surname (as stated on your NIN)</label>
                  <input value={form.kyc.surname} onChange={e => setKyc("surname", e.target.value)} />
                </div>
                <div className="form-field">
                  <label>First Name (as stated on your NIN)</label>
                  <input value={form.kyc.firstName} onChange={e => setKyc("firstName", e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Middle Name</label>
                  <input value={form.kyc.middleName} onChange={e => setKyc("middleName", e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Gender</label>
                  <select value={form.kyc.gender || ""} onChange={e => setKyc("gender", e.target.value)}>
                    <option value="">Select...</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Marital Status</label>
                  <select value={form.kyc.maritalStatus || ""} onChange={e => setKyc("maritalStatus", e.target.value)}>
                    <option value="">Select...</option>
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="divorced">Divorced</option>
                    <option value="widowed">Widow/Widower</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>WhatsApp Number</label>
                  <input value={form.kyc.whatsappNumber} onChange={e => setKyc("whatsappNumber", e.target.value)} />
                </div>
                <div className="form-field">
                  <label>National Identification Number (NIN)</label>
                  <input value={form.kyc.nin} onChange={e => setKyc("nin", e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Date of Birth</label>
                  <input type="date" value={form.kyc.dateOfBirth} onChange={e => setKyc("dateOfBirth", e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Residential Address</label>
                  <textarea rows={2} style={{ resize: "vertical" }} value={form.kyc.residentialAddress} onChange={e => setKyc("residentialAddress", e.target.value)} />
                </div>
              </Collapsible>

              <Collapsible title="🛵 KYC — Dispatcher Information" defaultOpen>
                <div className="form-field">
                  <label>Driving/Rider License Number</label>
                  <input value={form.kyc.drivingLicenseNumber} onChange={e => setKyc("drivingLicenseNumber", e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Bike/Vehicle Ownership</label>
                  <select value={form.kyc.vehicleOwnership || ""} onChange={e => setKyc("vehicleOwnership", e.target.value)}>
                    <option value="">Select...</option>
                    <option value="owned">I own the bike/vehicle</option>
                    <option value="leased">The bike/vehicle is leased/rented</option>
                    <option value="no_access">I do not have access to a bike/vehicle</option>
                  </select>
                </div>
                {form.vehicleType !== "bicycle" && (
                  <div className="form-field">
                    <label>Registration Number of Motorbike/Vehicle</label>
                    <input value={form.kyc.vehicleRegistrationNumber} onChange={e => setKyc("vehicleRegistrationNumber", e.target.value)} />
                  </div>
                )}
                <div className="form-field">
                  <label>Years of Experience Riding a Motorbike</label>
                  <select value={form.kyc.ridingExperienceYears || ""} onChange={e => setKyc("ridingExperienceYears", e.target.value)}>
                    <option value="">Select...</option>
                    {EXPERIENCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label>Years of Experience Driving a Car/Van</label>
                  <select value={form.kyc.drivingExperienceYears || ""} onChange={e => setKyc("drivingExperienceYears", e.target.value)}>
                    <option value="">Select...</option>
                    {EXPERIENCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label>Do you have a valid LASDRI?</label>
                  <div className="row">
                    <button type="button" className={form.kyc.hasValidLasdri === "yes" ? "success" : "secondary"} onClick={() => setKyc("hasValidLasdri", "yes")}>Yes</button>
                    <button type="button" className={form.kyc.hasValidLasdri === "no" ? "danger" : "secondary"} onClick={() => setKyc("hasValidLasdri", "no")}>No</button>
                  </div>
                </div>
              </Collapsible>

              <Collapsible title="📇 KYC — Guarantor/Reference" defaultOpen>
                <div className="form-field">
                  <label>Guarantor's Full Name</label>
                  <input value={form.kyc.guarantor.name} onChange={e => setGuarantor("name", e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Guarantor's Contact Telephone Number</label>
                  <input value={form.kyc.guarantor.phone} onChange={e => setGuarantor("phone", e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Guarantor's Contact Address</label>
                  <textarea rows={2} style={{ resize: "vertical" }} value={form.kyc.guarantor.address} onChange={e => setGuarantor("address", e.target.value)} />
                </div>
              </Collapsible>

              {requestError && <p className="error">{requestError}</p>}
              {requestMessage && <p style={{ color: "var(--success)", fontSize: "14px", marginBottom: "8px" }}>{requestMessage}</p>}
              <button type="submit" disabled={savingRequest || request?.status === "pending"}>
                {savingRequest ? "Submitting..." : request?.status === "pending" ? "Request Pending" : "Submit Change Request"}
              </button>
            </form>

            <form onSubmit={handlePasswordSubmit} className="delivery-card">
              <h3>Change Password</h3>
              <div className="form-field">
                <label>Current Password</label>
                <input type="password" value={passwordForm.currentPassword} onChange={e => setPasswordForm(f => ({ ...f, currentPassword: e.target.value }))} required />
              </div>
              <div className="form-field">
                <label>New Password</label>
                <input type="password" minLength={8} value={passwordForm.newPassword} onChange={e => setPasswordForm(f => ({ ...f, newPassword: e.target.value }))} required />
              </div>
              <div className="form-field">
                <label>Confirm New Password</label>
                <input type="password" minLength={8} value={passwordForm.confirmPassword} onChange={e => setPasswordForm(f => ({ ...f, confirmPassword: e.target.value }))} required />
              </div>
              {passwordError && <p className="error">{passwordError}</p>}
              {passwordMessage && <p style={{ color: "var(--success)", fontSize: "14px", marginBottom: "8px" }}>{passwordMessage}</p>}
              <button type="submit" disabled={savingPassword}>{savingPassword ? "Saving..." : "Change Password"}</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
